import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Users,
  UserCheck,
  UserX,
  Clock,
  Shield,
  CheckCircle,
  XCircle,
  Crown,
  TrendingUp,
  AlertTriangle,
  FileWarning,
  Trash2,
  Bell,
  UserPlus,
  Heart,
  Star,
  MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [activeTab, setActiveTab] = useState("notifications");
  const [isLoading, setIsLoading] = useState(true);
  const [incidentReviewStatus, setIncidentReviewStatus] = useState("");
  const [incidentAdminNotes, setIncidentAdminNotes] = useState("");
  
  // User deletion state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/dashboard");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statsRes, appsRes, usersRes, incidentsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/responders`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/incidents`),
        axios.get(`${API}/admin/notifications`)
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data);
      setAllUsers(usersRes.data);
      setIncidents(incidentsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error("Error loading admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? {...n, is_read: true} : n)
      );
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({...n, is_read: true})));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/admin/responders/${userId}/approve`);
      toast.success("Responder approved!");
      loadData();
      setSelectedApp(null);
    } catch (error) {
      toast.error("Failed to approve responder");
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.post(`${API}/admin/responders/${userId}/reject`);
      toast.success("Application rejected");
      loadData();
      setSelectedApp(null);
    } catch (error) {
      toast.error("Failed to reject application");
    }
  };

  const handlePromoteAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/promote`);
      toast.success("User promoted to admin!");
      loadData();
    } catch (error) {
      toast.error("Failed to promote user");
    }
  };

  const handleDemoteAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/demote`);
      toast.success("Admin privileges removed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to demote user");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    // Open confirmation dialog for single user
    setUsersToDelete([{ user_id: userId, name: userName }]);
    setShowDeleteDialog(true);
  };

  const handleDeleteSelected = () => {
    // Get selected user details for confirmation
    const usersDetails = allUsers
      .filter(u => selectedUsers.includes(u.user_id))
      .map(u => ({ user_id: u.user_id, name: u.name }));
    setUsersToDelete(usersDetails);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const userToDelete of usersToDelete) {
      try {
        await axios.delete(`${API}/admin/users/${userToDelete.user_id}`);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to delete ${userToDelete.name}:`, error);
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} user(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} user(s)`);
    }

    setShowDeleteDialog(false);
    setUsersToDelete([]);
    setSelectedUsers([]);
    setIsDeleting(false);
    loadData();
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    const selectableUsers = allUsers.filter(
      u => u.user_id !== user.user_id && u.email !== "bmagee@brcinvestments.com"
    );
    
    if (selectedUsers.length === selectableUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(selectableUsers.map(u => u.user_id));
    }
  };

  const handleReviewIncident = async () => {
    if (!selectedIncident || !incidentReviewStatus) return;
    
    try {
      await axios.put(`${API}/admin/incidents/${selectedIncident.incident_id}/review`, {
        review_status: incidentReviewStatus,
        admin_notes: incidentAdminNotes || null
      });
      toast.success("Incident review updated");
      loadData();
      setSelectedIncident(null);
      setIncidentReviewStatus("");
      setIncidentAdminNotes("");
    } catch (error) {
      toast.error("Failed to update incident review");
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      self_harm_ideation: "Self-Harm Ideation",
      harm_to_others: "Harm to Others",
      clinical_request: "Clinical Request",
      emergency_escalation: "Emergency Escalation",
      other_safety_concern: "Other Safety Concern"
    };
    return labels[category] || category;
  };

  const getActionLabel = (action) => {
    const labels = {
      displayed_escalation_modal: "Displayed escalation modal",
      "988_recommended": "988 recommended",
      "911_recommended": "911 recommended",
      session_paused: "Session paused",
      volunteer_disengaged: "Volunteer disengaged"
    };
    return labels[action] || action;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-red-100 text-red-700",
      reviewed: "bg-yellow-100 text-yellow-700",
      closed: "bg-green-100 text-green-700"
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const filteredApplications = applications.filter(app => {
    if (activeTab === "all") return true;
    return app.application_status === activeTab;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
              <div>
                <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  Admin Dashboard
                </span>
                <Badge variant="secondary" className="ml-2">Admin</Badge>
              </div>
              <span className="text-xs text-primary font-bold">™</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="card-valor">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total_users}</p>
                  </div>
                  <Users className="w-8 h-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-valor">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Apps</p>
                    <p className="text-2xl font-bold text-secondary">{stats.pending_applications}</p>
                  </div>
                  <Clock className="w-8 h-8 text-secondary opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-valor">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Responders</p>
                    <p className="text-2xl font-bold text-green-600">{stats.approved_responders}</p>
                  </div>
                  <UserCheck className="w-8 h-8 text-green-600 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-valor">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Open Incidents</p>
                    <p className="text-2xl font-bold text-red-600">{stats.open_incidents || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-600 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin/feedback")}
            className="flex items-center gap-2"
            data-testid="view-feedback-analytics-btn"
          >
            <Star className="w-4 h-4 text-yellow-500" />
            View Feedback Analytics
            <MessageSquare className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin/escalations")}
            className="flex items-center gap-2"
            data-testid="view-escalation-tracking-btn"
          >
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Escalation Tracking
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin/bulk-alerts")}
            className="flex items-center gap-2"
            data-testid="view-bulk-alerts-btn"
          >
            <Bell className="w-4 h-4 text-primary" />
            Bulk Alerts
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Alerts</span>
              {stats?.unread_notifications > 0 && (
                <Badge variant="destructive" className="ml-1">{stats.unread_notifications}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Pending</span>
              {stats?.pending_applications > 0 && (
                <Badge variant="secondary" className="ml-1">{stats.pending_applications}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">
              <UserCheck className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Approved</span>
            </TabsTrigger>
            <TabsTrigger value="rejected">
              <UserX className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Rejected</span>
            </TabsTrigger>
            <TabsTrigger value="incidents" className="flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              <span className="hidden sm:inline">Incidents</span>
              {stats?.open_incidents > 0 && (
                <Badge variant="destructive" className="ml-1">{stats.open_incidents}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab Content */}
          <TabsContent value="notifications" className="space-y-4">
            <Card className="card-valor">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Bell className="w-5 h-5 text-primary" />
                  Activity Notifications
                </CardTitle>
                {notifications.some(n => !n.is_read) && (
                  <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                    Mark All Read
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No notifications yet</p>
                    <p className="text-sm">You'll be notified when veterans sign up, apply as responders, or request support</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map(notif => (
                      <div 
                        key={notif.notification_id}
                        className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                          notif.is_read 
                            ? 'bg-muted/30 border-border' 
                            : 'bg-primary/5 border-primary/30'
                        }`}
                        onClick={() => handleMarkNotificationRead(notif.notification_id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            notif.type === 'new_user' ? 'bg-blue-100' :
                            notif.type === 'responder_application' ? 'bg-green-100' :
                            notif.type === 'crisis_request' ? 'bg-red-100' :
                            'bg-gray-100'
                          }`}>
                            {notif.type === 'new_user' && <UserPlus className="w-5 h-5 text-blue-600" />}
                            {notif.type === 'responder_application' && <Shield className="w-5 h-5 text-green-600" />}
                            {notif.type === 'crisis_request' && <Heart className="w-5 h-5 text-red-600" />}
                            {notif.type === 'incident' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${notif.is_read ? 'text-muted-foreground' : 'text-foreground'}`}>
                                {notif.title}
                              </p>
                              {!notif.is_read && (
                                <span className="w-2 h-2 bg-primary rounded-full"></span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab Content */}
          <TabsContent value="pending" className="space-y-4">
            {filteredApplications.length === 0 ? (
              <Card className="card-valor">
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending applications</p>
                </CardContent>
              </Card>
            ) : (
              filteredApplications.map(app => (
                <ApplicationCard 
                  key={app.user_id} 
                  app={app} 
                  onView={() => setSelectedApp(app)}
                  onApprove={() => handleApprove(app.user_id)}
                  onReject={() => handleReject(app.user_id)}
                  showActions={true}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {applications.filter(a => a.application_status === "approved").length === 0 ? (
              <Card className="card-valor">
                <CardContent className="py-12 text-center">
                  <UserCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No approved responders yet</p>
                </CardContent>
              </Card>
            ) : (
              applications.filter(a => a.application_status === "approved").map(app => (
                <ApplicationCard 
                  key={app.user_id} 
                  app={app} 
                  onView={() => setSelectedApp(app)}
                  showActions={false}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {applications.filter(a => a.application_status === "rejected").length === 0 ? (
              <Card className="card-valor">
                <CardContent className="py-12 text-center">
                  <UserX className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No rejected applications</p>
                </CardContent>
              </Card>
            ) : (
              applications.filter(a => a.application_status === "rejected").map(app => (
                <ApplicationCard 
                  key={app.user_id} 
                  app={app} 
                  onView={() => setSelectedApp(app)}
                  showActions={false}
                />
              ))
            )}
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="space-y-4">
            <Card className="card-valor">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Safety Incidents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incidents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No incidents logged</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {incidents.map(incident => (
                      <div 
                        key={incident.incident_id}
                        className="p-4 bg-muted rounded-lg hover:bg-muted/80 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedIncident(incident);
                          setIncidentReviewStatus(incident.review_status);
                          setIncidentAdminNotes(incident.admin_notes || "");
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge className={getStatusBadge(incident.review_status)}>
                                {incident.review_status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {incident.incident_id}
                              </span>
                            </div>
                            <p className="font-medium text-foreground">
                              {getCategoryLabel(incident.trigger_category)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {getActionLabel(incident.action_taken)}
                            </p>
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <p>{new Date(incident.created_at).toLocaleDateString()}</p>
                            <p>{new Date(incident.created_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="card-valor">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Manrope' }}>All Users</CardTitle>
                {selectedUsers.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedUsers.length})
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Select All */}
                <div className="flex items-center gap-2 pb-3 mb-3 border-b">
                  <Checkbox
                    id="select-all"
                    checked={selectedUsers.length > 0 && selectedUsers.length === allUsers.filter(u => u.user_id !== user.user_id && u.email !== "bmagee@brcinvestments.com").length}
                    onCheckedChange={toggleSelectAll}
                  />
                  <label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer">
                    Select all deletable users
                  </label>
                </div>
                
                <div className="space-y-3">
                  {allUsers.map(u => {
                    const canDelete = u.user_id !== user.user_id && u.email !== "bmagee@brcinvestments.com";
                    const isSelected = selectedUsers.includes(u.user_id);
                    
                    return (
                      <div 
                        key={u.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isSelected ? 'bg-destructive/10 border border-destructive/30' : 'bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {canDelete && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleUserSelection(u.user_id)}
                            />
                          )}
                          <Avatar>
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-primary text-white">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{u.name}</p>
                              {u.is_admin && (
                                <Crown className="w-4 h-4 text-yellow-500" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            {u.address && (
                              <p className="text-xs text-muted-foreground">{u.address}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={u.role === "responder" ? "default" : "secondary"}>
                            {u.role}
                          </Badge>
                          {canDelete && (
                            <>
                              {u.is_admin ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDemoteAdmin(u.user_id)}
                                >
                                  Remove Admin
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePromoteAdmin(u.user_id)}
                                >
                                  <Crown className="w-4 h-4 mr-1" />
                                  Make Admin
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                onClick={() => handleDeleteUser(u.user_id, u.name)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Application Detail Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={selectedApp.picture} />
                    <AvatarFallback className="bg-primary text-white">
                      {getInitials(selectedApp.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p>{selectedApp.name}</p>
                      {selectedApp.applicant_type === "civilian" && (
                        <Badge variant="outline" className="text-xs">Civilian</Badge>
                      )}
                    </div>
                    <p className="text-sm font-normal text-muted-foreground">{selectedApp.email}</p>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  Applied {new Date(selectedApp.applied_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Veteran-specific fields */}
                {selectedApp.applicant_type !== "civilian" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Military Branch</p>
                      <p className="font-medium">{selectedApp.military_branch || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Years of Service</p>
                      <p className="font-medium">{selectedApp.years_of_service || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Discharge Status</p>
                      <p className="font-medium">{selectedApp.discharge_status || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{selectedApp.zip_code || "Not provided"}</p>
                    </div>
                  </div>
                )}

                {/* Civilian-specific fields */}
                {selectedApp.applicant_type === "civilian" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Professional Background</p>
                      <p className="font-medium">{selectedApp.professional_background || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Years of Experience</p>
                      <p className="font-medium">{selectedApp.years_of_experience || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Organization</p>
                      <p className="font-medium">{selectedApp.organization || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{selectedApp.zip_code || "Not provided"}</p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Certifications</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedApp.certifications?.length > 0 ? (
                      selectedApp.certifications.map(cert => (
                        <Badge key={cert} variant="secondary">{cert}</Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">None listed</span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Availability</p>
                  <p className="font-medium">
                    {selectedApp.availability_days?.join(", ") || "Not specified"}
                  </p>
                  <p className="text-sm text-muted-foreground">{selectedApp.availability_hours}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Bio</p>
                  <p className="text-foreground whitespace-pre-wrap">{selectedApp.bio}</p>
                </div>

                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  {selectedApp.background_check_consent ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm">Consented to background check</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-destructive" />
                      <span className="text-sm">No background check consent</span>
                    </>
                  )}
                </div>
              </div>

              {selectedApp.application_status === "pending" && (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => handleReject(selectedApp.user_id)}
                    className="text-destructive"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedApp.user_id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Incident Review Dialog */}
      <IncidentReviewDialog
        incident={selectedIncident}
        isOpen={!!selectedIncident}
        onClose={() => setSelectedIncident(null)}
        reviewStatus={incidentReviewStatus}
        setReviewStatus={setIncidentReviewStatus}
        adminNotes={incidentAdminNotes}
        setAdminNotes={setIncidentAdminNotes}
        onSubmit={handleReviewIncident}
        getCategoryLabel={getCategoryLabel}
        getActionLabel={getActionLabel}
        getStatusBadge={getStatusBadge}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </AlertDialogTitle>
            <AlertDialogDescription>
              {usersToDelete.length === 1 ? (
                <span>
                  Are you sure you want to delete <strong>{usersToDelete[0]?.name}</strong>? 
                  This action cannot be undone.
                </span>
              ) : (
                <div className="space-y-2">
                  <span>Are you sure you want to delete these {usersToDelete.length} users? This action cannot be undone.</span>
                  <ul className="mt-2 space-y-1 text-sm max-h-40 overflow-y-auto">
                    {usersToDelete.map(u => (
                      <li key={u.user_id} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-destructive rounded-full"></span>
                        {u.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : `Delete ${usersToDelete.length > 1 ? `(${usersToDelete.length})` : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Application Card Component
const ApplicationCard = ({ app, onView, onApprove, onReject, showActions }) => {
  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "approved":
        return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getApplicantTypeInfo = () => {
    if (app.applicant_type === "civilian") {
      return `${app.professional_background || "Civilian"}${app.years_of_experience ? ` • ${app.years_of_experience} years experience` : ""}`;
    }
    return `${app.military_branch || "Veteran"}${app.years_of_service ? ` • ${app.years_of_service} years` : ""}`;
  };

  return (
    <Card className="card-valor">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="w-12 h-12">
              <AvatarImage src={app.picture} />
              <AvatarFallback className="bg-primary text-white">
                {getInitials(app.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-foreground">{app.name}</p>
                {app.applicant_type === "civilian" && (
                  <Badge variant="outline" className="text-xs">Civilian</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{getApplicantTypeInfo()}</p>
              <div className="flex items-center gap-2 mt-1">
                {getStatusBadge(app.application_status)}
                {app.is_available && (
                  <Badge variant="outline" className="text-green-600 border-green-600">Available</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onView}>
              View Details
            </Button>
            {showActions && (
              <>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-destructive"
                  onClick={onReject}
                >
                  Reject
                </Button>
                <Button 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={onApprove}
                >
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Incident Review Dialog Component
const IncidentReviewDialog = ({ 
  incident, 
  isOpen, 
  onClose, 
  reviewStatus, 
  setReviewStatus, 
  adminNotes, 
  setAdminNotes, 
  onSubmit,
  getCategoryLabel,
  getActionLabel,
  getStatusBadge
}) => {
  if (!incident) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Incident Review
          </DialogTitle>
          <DialogDescription>
            Review and update the status of this safety incident
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Incident Details */}
          <div className="space-y-3 p-4 bg-muted rounded-lg">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Incident ID</span>
              <span className="text-sm font-mono">{incident.incident_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Date/Time</span>
              <span className="text-sm">{new Date(incident.created_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Category</span>
              <span className="text-sm font-medium">{getCategoryLabel(incident.trigger_category)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Trigger Type</span>
              <span className="text-sm capitalize">{incident.trigger_type?.replace(/_/g, ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Action Taken</span>
              <span className="text-sm">{getActionLabel(incident.action_taken)}</span>
            </div>
            {incident.session_id && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Session ID</span>
                <span className="text-sm font-mono">{incident.session_id}</span>
              </div>
            )}
            {incident.system_notes && (
              <div className="pt-2 border-t">
                <span className="text-sm text-muted-foreground">System Notes</span>
                <p className="text-sm mt-1">{incident.system_notes}</p>
              </div>
            )}
          </div>

          {/* Review Status */}
          <div className="space-y-2">
            <Label>Review Status</Label>
            <Select value={reviewStatus} onValueChange={setReviewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label>Admin Notes</Label>
            <Textarea
              placeholder="Add your review notes..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-primary">
            Update Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
